import { convertDateTimeToUTC, datetimeFormat } from '../helpers';
import { transformedMedia } from './mediaLibrary';

/**
 * Transform a static landing page object into a more usable format.
 * @param {Object} data The static landing page object.
 * @param {number} data.id The ID of the static landing page.
 * @param {string} data.name The name of the static landing page.
 * @param {string} data.slug The slug of the static landing page.
 * @param {string} data.type The type of the static landing page.
 * @param {string|null} data.start_at The start date of the static landing page.
 * @param {string|null} data.end_at The end date of the static landing page.
 * @param {number} data.is_active The active status of the static landing page (1 for active, 0 for inactive).
 * @param {Object|null} data.footer_column The footer column information.
 * @param {Object[]} data.children The children of the static landing page.
 * @returns {Object} A transformed static landing page object.
 */
export const transformStaticLandingPage = (data) => {
	if (!data) return null;

	const {
		id,
		name,
		name_en: nameEn,
		name_th: nameTh,
		custom_link: customLink,
		slug,
		type,
		start_at: startAt,
		end_at: endAt,
		is_active: isActive,
		is_visible: isVisible,
		is_header_visible: isHeaderVisible,
		is_sidebar_visible: isSidebarVisible,
		footer_column: footerColumn,
		children,
		seo,
	} = data;

	return {
		id,
		name: (nameTh && nameEn) ? `${nameTh} / ${nameEn}` : (name || nameEn || nameTh),
		nameEn,
		nameTh,
		customUrl: customLink || null,
		slug,
		type,
		startAt,
		endAt,
		isActive: isActive === 1 || isActive === true,
		isVisible: isVisible === 1 || isVisible === true,
		isHeaderVisible: isHeaderVisible === 1 || isHeaderVisible === true,
		isSidebarVisible: isSidebarVisible === 1 || isSidebarVisible === true,
		footerColumn,
		seo: seo ? {
			metaTitleEn: seo.title_en,
			metaTitleTh: seo.title_th,
			metaKeyEn: seo.keyword_en,
			metaKeyTh: seo.keyword_th,
			metaDescEn: seo.description_en,
			metaDescTh: seo.description_th,
			metaImage: seo.image && seo.image[0] ? transformedMedia(seo.image[0]) : null,
		} : {},
		children: children ? children.map(transformStaticLandingPage).filter((item) => item !== null) : [],
	};
};

/**
 * Transform a list of static landing page objects.
 * @param {Object[]} payload A list of static landing page objects from the API.
 * @returns {Object[]} A list of transformed static landing page objects.
 */
export const transformedStaticLandingPages = (payload = []) => {
	return payload.map(transformStaticLandingPage).filter((item) => item !== null);
};

/**
 * Transform step data of static landing page to API format
 * @param {Object} steps A map of steps and form data
 * @returns {Object} JSON payload for API
 */
export const transformedStaticLandingPageDataToAPI = (steps = {}) => {
	const generalInfo = steps[1]?.formData || {};

	const startAt = generalInfo.startDate && generalInfo.startTime ? convertDateTimeToUTC(generalInfo.startDate, generalInfo.startTime) : null;
	const endAt = generalInfo.endDate && generalInfo.endTime ? convertDateTimeToUTC(generalInfo.endDate, generalInfo.endTime) : null;

	return {
		type: 'landing',
		name: generalInfo.nameEn,
		name_en: generalInfo.nameEn,
		name_th: generalInfo.nameTh,
		custom_link: generalInfo.customUrl || null,
		slug: generalInfo.slug,
		is_active: generalInfo.isActive ? 1 : 0,
		is_visible: generalInfo.isVisible,
		is_header_visible: generalInfo.isHeaderVisible,
		is_sidebar_visible: generalInfo.isSidebarVisible,
		start_at: startAt,
		end_at: endAt,
		seo: {
			title_en: generalInfo.metaTitleEn || undefined,
			title_th: generalInfo.metaTitleTh || undefined,
			keyword_en: generalInfo.metaKeyEn || undefined,
			keyword_th: generalInfo.metaKeyTh || undefined,
			description_en: generalInfo.metaDescEn || undefined,
			description_th: generalInfo.metaDescTh || undefined,
			image: generalInfo.metaImage ? generalInfo.metaImage.id : null,
		},
	};
};

/**
 * Transform static landing page API data to multi-step form data
 * @param {Object} data API data
 * @returns {Object} Multi-step form data
 */
export const transformedStaticLandingPageToDataMultipleStep = (data = {}) => {
	if (!data) return null;

	const transformed = transformStaticLandingPage(data);

	return {
		steps: {
			1: {
				formData: {
					id: transformed.id,
					nameEn: transformed.nameEn,
					nameTh: transformed.nameTh,
					customUrl: transformed.customUrl,
					slug: transformed.slug,
					isActive: transformed.isActive,
					isVisible: transformed.isVisible,
					isHeaderVisible: transformed.isHeaderVisible,
					isSidebarVisible: transformed.isSidebarVisible,
					startDate: transformed.startAt ? new Date(datetimeFormat(transformed.startAt, 'YYYY-MM-DD')) : null,
					startTime: transformed.startAt ? datetimeFormat(transformed.startAt, 'HH:mm') : '00:00',
					endDate: transformed.endAt ? new Date(datetimeFormat(transformed.endAt, 'YYYY-MM-DD')) : null,
					endTime: transformed.endAt ? datetimeFormat(transformed.endAt, 'HH:mm') : '23:59',
					...transformed.seo,
				},
			},
			2: {
				formData: null, // Widgets are fetched by the component itself in edit mode if needed
			},
		},
	};
};
